import React, { useEffect, useRef, useMemo, useState } from "react";
import { IonIcon, IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Statistic,
  Progress,
  Typography,
  Space,
  Row,
  Col,
  Result,
  Spin,
  Select,
  Tooltip as AntdTooltip,
  notification,
} from "antd";
import {
  ProductOutlined,
  ShopOutlined,
  PercentageOutlined,
  AreaChartOutlined,
  InfoCircleOutlined,
  AuditOutlined,
  UsergroupAddOutlined,
  Loading3QuartersOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { giftOutline, walletOutline } from "ionicons/icons";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
//
import log from "../../utils/log";
import c from "../../utils/common";
import moment from "moment";
import metaData from "../../data/metaData";
import { isEmpty } from "lodash";

//
import useAuthStore from "../../states/useAuth";
import { arrowForward } from "ionicons/icons";
import useCompany from "../../hooks/useCompany";
import useCompanyStore from "../../states/useCompany";

const { Title, Text } = Typography;
const { Option } = Select;

const genderData = [
  { age: "15-24", male: 100, female: 80 },
  { age: "25-34", male: 90, female: 70 },
  { age: "35-44", male: 70, female: 60 },
  { age: "45-54", male: 50, female: 40 },
  { age: "55-64", male: 30, female: 20 },
  { age: "+64", male: 10, female: 5 },
];

const customerInterestData = [
  { subject: "Fashion", male: 19, female: 310 },
  { subject: "Cosmetics", male: 18, female: 530 },
  { subject: "Food", male: 518, female: 330 },
  { subject: "Watches", male: 186, female: 130 },
  { subject: "Others", male: 99, female: 100 },
  { subject: "Cars", male: 218, female: 85 },
  { subject: "Tech", male: 226, female: 58 },
];

const TopProductItem = ({ item, i }) => {
  return (
    <Col span={24}>
      <Row justify="space-between" align="middle">
        {i === 0 ? (
          <img
            src={metaData.blobPreURL + "clientUpload-" + undefined + ".jpeg" + metaData.blobPostURL}
            alt="New product"
            style={{ width: "100%", height: "200px", objectFit: "cover" }}
          />
        ) : null}
        <Col span={20}>
          <div style={{ display: "flex" }}>
            {i !== 0 ? (
              <img
                src={metaData.blobPreURL + "clientUpload-" + undefined + ".jpeg" + metaData.blobPostURL}
                alt={`Product ${item?.name}`}
                style={{ width: "50px", height: "50px", marginRight: "16px" }}
              />
            ) : null}
            <div>
              {i === 0 ? <Title level={4}>{item?.name}</Title> : <Text strong>{item?.name}</Text>}
              <div>
                <Text strong>$ {item?.price}</Text>
                <Text style={{ marginLeft: "8px" }}>
                  {item?.stock > 0 ? (
                    <>
                      <Text type="success" style={{ marginRight: "0.5rem" }}>
                        in stock
                      </Text>
                      <Text>{item?.stock}</Text>
                    </>
                  ) : (
                    <Text type="warning">sold out</Text>
                  )}
                </Text>
              </div>
            </div>
          </div>
        </Col>
        <Col span={4} style={{ textAlign: "end" }}>
          <Text strong>${item?.revenue}</Text>
        </Col>
      </Row>
    </Col>
  );
};

const GetStartedBanner = () => {
  const history = useHistory();
  const { user } = useAuthStore();

  return (
    <Card
      style={{
        background: "#f0f8ff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", zIndex: 1 }}>
        <Title level={4}>Your company profile is under review</Title>
        <Row style={{ width: "80%" }}>
          <Text>You can add more shops while we review.</Text>
        </Row>
        <Row style={{ width: "100%" }}>
          <Button
            size="large"
            color="primary"
            variant="solid"
            style={{
              marginTop: "1rem",
              width: "5rem",
              //color: "white", background: "var(--ion-color-primary-600)"
            }}
            onClick={() => {
              history.push("/merchantUser/shop/request/" + user?.newUserSetting?.initialRequestId);
            }}
          >
            View
          </Button>
        </Row>
      </div>

      <AuditOutlined
        style={{
          position: "absolute",
          bottom: "0px",
          right: "-10px",
          width: "110px",
          height: "110px",
          opacity: 0.2,
          fontSize: "7.5rem",
        }}
      />
    </Card>
  );
};

const UpgradeBanner = () => {
  return (
    <Card
      style={{
        background: "#f0f8ff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", zIndex: 1 }}>
        <Title level={4}>Manage your business effortlessly, anytime, anywhere</Title>
        <Text>Essential packages for online store management</Text>
        <Row style={{ width: "100%" }}>
          <Button type="primary" style={{ marginTop: "16px", background: "#2c3e50", borderColor: "#2c3e50" }}>
            View Plan
          </Button>
        </Row>
      </div>

      <AreaChartOutlined
        style={{
          position: "absolute",
          bottom: "-10px",
          right: "-30px",
          width: "150px",
          height: "150px",
          opacity: 0.2,
          fontSize: "7.5rem",
        }}
      />
    </Card>
  );
};

const QuickAction = () => {
  const history = useHistory();
  const { user } = useAuthStore(); // Assuming `company` info is also available in the store
  const { company, getMaxShopExpiry } = useCompanyStore();
  // Define actions, removing the isEnable logic from here
  const allActions = [
    {
      id: "shop",
      title: "Add New Shop",
      description: "Set up a shop and manage sales effortlessly.",
      icon: <ShopOutlined />,
      permissionRequired: user?.permission?.shop,
      needsCompanyId: false,
      onClick: () => history.push("/merchantUser/shop/request/new"),
    },
    {
      id: "user",
      title: "User & Permission",
      description: "Create accounts with customized permissions.",
      icon: <UsergroupAddOutlined />,
      permissionRequired: user?.permission?.staff,
      needsCompanyId: true,
      onClick: () => history.push("/merchantUser/user/new"),
    },
    {
      id: "product",
      title: "Add New Product",
      description: "Quickly add and showcase new products.",
      icon: <PercentageOutlined />,
      permissionRequired: user?.permission?.product?.issue,
      needsCompanyId: true,
      onClick: () => history.push("/merchantUser/product/request/new"),
    },
    {
      id: "promotionCode",
      title: "Promotion Code",
      description: "Create and manage promotion codes to attract customers.",
      icon: <PercentageOutlined />,
      permissionRequired: user?.permission?.promotionCode,
      needsCompanyId: true,
      onClick: () => history.push("/merchantUser/promotionCode"),
    },
    {
      id: "estamp",
      title: "Coin",
      description: "Launch a digital loyalty program for customer retention.",
      icon: <IonIcon icon={walletOutline} />,
      permissionRequired: user?.permission?.estamp?.issue,
      needsCompanyId: true,
      onClick: () => history.push("/merchantUser/coin"),
    },
    {
      id: "report",
      title: "Reports",
      description: "Monitor voucher sales, transfers, redemptions, and eStamp usage.",
      icon: <AreaChartOutlined />,
      permissionRequired: user?.permission?.report?.length > 0,
      needsCompanyId: true,
      onClick: () => history.push("/merchantUser/report/distribute"),
    },
  ];

  // Determine if the company's shop expiry is valid
  const maxShopExpiry = getMaxShopExpiry(company);
  const isShopExpiryValid = useMemo(() => {
    return maxShopExpiry && moment(maxShopExpiry).isAfter(moment());
  }, [company]);

  // Filter out actions that lack required permissions
  const visibleActions = allActions.filter((action) => action.permissionRequired);

  return (
    <Row gutter={[16, 16]} style={{ width: "100%", margin: "1rem 0" }}>
      <Col span={24}>
        <Title level={3}>Quick Action</Title>
      </Col>
      {visibleActions.map((e, i) => {
        return (
          <Col key={i} span={8} style={{ display: "flex" }}>
            <Card
              hoverable
              style={{
                flex: 1,
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
              }}
              onClick={e.onClick}
            >
              <Row justify="space-between" align="middle" style={{ zIndex: 2, position: "relative" }}>
                <Text strong>
                  {e.icon}&nbsp;&nbsp;{e.title}
                </Text>
                <IonIcon icon={arrowForward} />
                <Text type="secondary" style={{ marginTop: "1rem" }}>
                  {e.description}
                </Text>
              </Row>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

const CustomerStatistic = () => {
  return (
    <>
      <Col span={24}>
        <Title level={3}>Customer</Title>
      </Col>
      <Card
      // title={<Title level={3}>Customers</Title>}
      // extra={<Button type="link">See all</Button>}
      >
        <Row gutter={[16, 16]} style={{ width: "100%" }}>
          {/* <Col span={24}>
          <Title level={3}>Customers</Title>
        </Col> */}
          <Col xs={24} sm={12} md={8}>
            <Statistic
              title={
                <AntdTooltip title="Customers who made a purchase or liked your shops">
                  <span>
                    Total Customers <InfoCircleOutlined />
                  </span>
                </AntdTooltip>
              }
              value={2100}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Statistic
              title={
                <AntdTooltip title="Customers with your shop in their favorites">
                  <span>
                    Add to Favorites <InfoCircleOutlined />
                  </span>
                </AntdTooltip>
              }
              value={510}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Statistic title="New Customers (7d)" value={58} />
          </Col>
        </Row>
      </Card>
    </>
  );
};

const CustomerMetric = () => {
  return (
    <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
      <Col span={12}>
        <Card title="Age and Gender">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={genderData} layout="vertical">
              <XAxis type="number" />
              <YAxis dataKey="age" type="category" />
              <Tooltip />
              <Legend />
              <Bar dataKey="male" fill="#8884d8" name="Male" />
              <Bar dataKey="female" fill="#82ca9d" name="Female" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>
      <Col span={12}>
        <Card title="Customer Interest">
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={customerInterestData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" />
              <PolarRadiusAxis />
              <Radar name="male" dataKey="male" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
              <Radar name="female" dataKey="female" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      </Col>
    </Row>
  );
};

const TopProduct = () => {
  return (
    <Card title={<Title level={4}>Top 5 Products</Title>} extra={<Button type="link">See all</Button>}>
      <Row gutter={[16, 16]}>
        {[
          { name: "Strike a yogi pose", price: 14, stock: 3, revenue: 7581 },
          { name: "Vintage 3D computer", price: 64, stock: 0, revenue: 5122 },
          { name: "Vintage 3D computer", price: 114, stock: 56, revenue: 1255 },
          { name: "Vintage 3D computer", price: 11, stock: 110, revenue: 110 },
          { name: "Vintage 3D computer", price: 200, stock: 3, revenue: 200 },
        ].map((item, i) => (
          <TopProductItem item={item} key={i} i={i} />
        ))}
      </Row>
    </Card>
  );
};

const Home = () => {
  const { auth, user, updateUser } = useAuthStore();
  const { getHomePageData } = useCompany();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Table
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [data, setData] = useState([]);

  //
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);

  const pageRef = useRef();

  const generateData = (num) => {
    const shops = [];
    const categories = ["shop", "local", "fashion", "food", "tasty", "electronics", "home"];
    const statuses = ["pending", "approved", "needRevision"]; // Array of statuses

    for (let i = 1; i <= num; i++) {
      const shop = {
        key: i,
        id: c.generateId(4, 6),
        company: { name: `Company ${i}` },
        shop: { name: `Shop ${i}` },
        status: statuses[Math.floor(Math.random() * statuses.length)], // Randomize status
        category: categories[i % categories.length],
        lastModifyTime: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString(), // Different lastModifyTime dates
      };
      shops.push(shop);
    }
    log.debug(shops);
    return shops;
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  };

  const handleActionClick = (key) => {
    log.debug(key);
  };

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const onMount = async () => {
    try {
      setIsFetchingUser(true);
      await getHomePageData();
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingUser(false);
    }
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = (event) => {
      updateUser("whole", {});
      // const message = "Are you sure you want to leave? Changes may not be saved.";
      // event.preventDefault();
      // event.returnValue = message; // This will trigger the prompt with the message
      // return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    onMount();
  }, []);

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingUser}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {!user || isEmpty(user) ? null : isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : isFetchingUser ? null : (
        <>
          <Title level={2}>Welcome, {user?.name}</Title>
          <Row gutter={[16, 16]}>
            <Col span={16}>
              {!user?.companyId ? <GetStartedBanner /> : null}
              {/* Quick Action */}
              <QuickAction />

              {/* Customer Total */}
              {/* {user?.companyId ? <CustomerStatistic /> : null} */}

              {/* Customer Metric */}
              {/* {user?.companyId ? <CustomerMetric /> : null} */}

              {/* Approval */}
              {/* <Row gutter={[16, 16]} style={{ margin: "1rem 0" }}>
                    <Card
                      style={{ width: "100%" }}
                      title="Approval"
                      // extra={<Button type="link">View all</Button>}
                    >
                      <Table
                        columns={columns}
                        dataSource={data}
                        pagination={{
                          size: 5,
                        }}
                      />
                    </Card>
                  </Row> */}
            </Col>

            <Col span={8}>
              {/* Top Product */}
              {/* {user?.companyId ? <TopProduct /> : null} */}
            </Col>
          </Row>

          {/* <Card style={{ marginTop: "24px" }}>
                <Title level={4}>Get more customers!</Title>
                <Text>
                  50% of new customers explore products on the social media network. Gain your network right now! 🔥
                </Text>
                <div style={{ marginTop: "16px" }}>
                  <Button icon={<FacebookOutlined />} style={{ marginRight: "8px" }}>
                    Facebook
                  </Button>
                  <Button icon={<femaleOutlined />} style={{ marginRight: "8px" }}>
                    female
                  </Button>
                  <Button icon={<InstagramOutlined />}>Instagram</Button>
                </div>
              </Card> */}
        </>
      )}
    </div>
  );
};

export default Home;
